/**
 * Journey Error Component
 *
 * Shown when generation fails. Options:
 * - Retry generation
 * - View fallback journey (if available)
 * - Go to dashboard (explore other tabs)
 * - View lite preview (when rate limited)
 */

import { AlertTriangle, RefreshCw, LayoutDashboard, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RateLimitPreview } from './RateLimitPreview';
import type { PreComputedJourneyData, FireSnapshot } from '@/lib/ai/schemas/factPack.schema';

interface JourneyErrorProps {
  error: string;
  rateLimitInfo?: { reason: string; retryAfter: number } | null;
  hasFallback: boolean;
  preComputedData?: PreComputedJourneyData | null;
  fireSnapshot?: FireSnapshot | null;
  onRetry: () => void;
  onViewFallback: () => void;
  onGoToDashboard: () => void;
}

export function JourneyError({
  error,
  rateLimitInfo,
  hasFallback,
  preComputedData,
  fireSnapshot,
  onRetry,
  onViewFallback,
  onGoToDashboard,
}: JourneyErrorProps) {
  const isRateLimited = !!rateLimitInfo;

  // Format retry time
  const formatRetryTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)} minutes`;
    if (seconds < 86400) return `${Math.ceil(seconds / 3600)} hours`;
    return `${Math.ceil(seconds / 86400)} days`;
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6 text-center">
      <div
        className={`p-4 rounded-full ${isRateLimited ? 'bg-amber-100' : 'bg-red-100'}`}
      >
        {isRateLimited ? (
          <Clock className="w-8 h-8 text-amber-600" />
        ) : (
          <AlertTriangle className="w-8 h-8 text-red-600" />
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-slate-800">
          {isRateLimited ? 'Generation Limit Reached' : 'Generation Failed'}
        </h2>
        <p className="text-sm text-slate-600 max-w-md">
          {isRateLimited
            ? rateLimitInfo.reason
            : error || 'Something went wrong while generating your journey.'}
        </p>
        {isRateLimited && rateLimitInfo.retryAfter > 0 && (
          <p className="text-sm text-slate-500">
            Try again in {formatRetryTime(rateLimitInfo.retryAfter)}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3 justify-center">
        {!isRateLimited && (
          <Button onClick={onRetry} variant="default">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        )}

        {hasFallback && (
          <Button onClick={onViewFallback} variant="outline">
            View Basic Journey
          </Button>
        )}

        <Button onClick={onGoToDashboard} variant="outline">
          <LayoutDashboard className="w-4 h-4 mr-2" />
          Explore Dashboard
        </Button>
      </div>

      {/* Show lite preview for rate-limited users */}
      {isRateLimited && (preComputedData || fireSnapshot) && (
        <RateLimitPreview 
          preComputedData={preComputedData ?? null} 
          fireSnapshot={fireSnapshot ?? null}
          onGoToDashboard={onGoToDashboard} 
        />
      )}

      {!isRateLimited && (
        <p className="text-xs text-slate-400 max-w-sm">
          Your FIRE analysis, projections, and all 7 dashboard tabs are still
          available.
        </p>
      )}
    </div>
  );
}

export default JourneyError;
